import { ApiError } from "./types";

export interface SopAiResponse<T> {
  data: T;
  demoMode: boolean;
  correlationId: string | null;
}

export interface SopAiClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export type SopProcedureLifecycleState = "DRAFT" | "ACTIVE" | "RETIRED";
export type SopVersionState = "DRAFT" | "IN_REVIEW" | "APPROVED" | "PUBLISHED" | "SUPERSEDED";
export type SopRunState = "IN_PROGRESS" | "BLOCKED" | "COMPLETED" | "ABANDONED";
export type SopRunOutcome = "PASSED" | "PASSED_WITH_DEVIATION" | "FAILED";
export type SopStepResult = "DONE" | "SKIPPED" | "NOT_APPLICABLE" | "FAILED";
export type SopCriticality = "CRITICAL" | "HIGH" | "MODERATE" | "LOW";
export type SopDataClassification = "public" | "internal" | "confidential" | "restricted";
export type SopDeviationDecision = "ACCEPTED" | "REJECTED";

export const SOP_CRITICALITIES: readonly SopCriticality[] = ["CRITICAL", "HIGH", "MODERATE", "LOW"];
export const SOP_DATA_CLASSIFICATIONS: readonly SopDataClassification[] = [
  "public",
  "internal",
  "confidential",
  "restricted",
];
export const SOP_STEP_RESULTS: readonly SopStepResult[] = ["DONE", "SKIPPED", "NOT_APPLICABLE", "FAILED"];

export interface SopProcedureCategory {
  id?: string;
  key: string;
  label?: string;
}
export interface SopProcedure {
  id: string;
  organizationId: string;
  title: string;
  purpose?: string;
  category?: SopProcedureCategory;
  lifecycleState: SopProcedureLifecycleState;
  ownerUserId?: string;
  ownerName?: string;
  criticality?: SopCriticality;
  dataClassification?: SopDataClassification;
  /** Optimistic concurrency token the backend expects back on every write. */
  version: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}
/** A step as an authored version declares it. */
export interface SopStep {
  id: string;
  versionId?: string;
  stepOrder: number;
  instruction: string;
  expectedInputs?: string;
  isBlocking?: boolean;
  requiresEvidence?: boolean;
  requiresVerification?: boolean;
  [key: string]: unknown;
}
/** A step as a person performing a run recorded it. */
export interface SopStepRecord {
  id: string;
  runId: string;
  stepId: string;
  result: SopStepResult;
  notes?: string;
  performerId?: string;
  performedAt?: string;
  verifierId?: string | null;
  verifiedAt?: string | null;
  [key: string]: unknown;
}
export interface SopStepInput {
  stepOrder: number;
  instruction: string;
  expectedInputs?: string;
  isBlocking?: boolean;
  requiresEvidence?: boolean;
  requiresVerification?: boolean;
}
export interface SopProcedureVersion {
  id: string;
  procedureId: string;
  versionNumber: number;
  state: SopVersionState;
  changeReason?: string;
  effectiveFrom?: string | null;
  steps?: SopStep[];
  version: number;
  [key: string]: unknown;
}
export interface SopRun {
  id: string;
  organizationId: string;
  procedureId: string;
  versionId: string;
  subjectType?: string | null;
  subjectReference?: string | null;
  state: SopRunState;
  outcome?: SopRunOutcome | null;
  performerId?: string;
  version: number;
  startedAt?: string;
  [key: string]: unknown;
}
export interface SopRunEvidence {
  id: string;
  runId: string;
  stepId?: string | null;
  documentId: string;
  documentChecksum?: string;
  capturedBy?: string;
  capturedAt?: string;
  note?: string;
  [key: string]: unknown;
}
export interface SopDeviation {
  id: string;
  runId: string;
  stepId?: string | null;
  whatWasDone?: string;
  why?: string;
  actorId?: string;
  disposition?: SopDeviationDecision | null;
  dispositionRationale?: string | null;
  disposedBy?: string | null;
  [key: string]: unknown;
}
export interface SopControlLink {
  id: string;
  procedureId: string;
  controlId: string;
  state: "PROPOSED" | "VERIFIED" | "REVOKED" | string;
  [key: string]: unknown;
}
export interface SopPublication {
  id?: string;
  eventType?: string;
  status?: string;
  refused?: boolean;
  [key: string]: unknown;
}
export interface SopAiList<T> {
  [key: string]: T[] | { limit: number; offset: number; returned: number } | undefined;
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text) as unknown; } catch { return null; }
}

function raise(status: number, data: unknown): never {
  const error = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)?.error;
  throw new ApiError(status, error?.code ?? "error", error?.message ?? "SOP AI could not complete that request.", error?.details);
}

function query(params: Record<string, string | number | undefined>): string {
  const result = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== "") result.set(key, String(value));
  const encoded = result.toString();
  return encoded ? `?${encoded}` : "";
}

export class SopAiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized: (() => void) | undefined;

  constructor(options: SopAiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.getToken = options.getToken ?? (() => null);
    this.onUnauthorized = options.onUnauthorized;
  }

  private async request<T>(method: string, orgId: string, subpath: string, body?: unknown): Promise<SopAiResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/organizations/${orgId}/sop-ai/${subpath.replace(/^\/+/, "")}`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (response.status === 401) this.onUnauthorized?.();
    const data = await parse(response);
    if (!response.ok) raise(response.status, data);
    return {
      data: data as T,
      demoMode: response.headers.get("x-sop-ai-mode") === "demo",
      correlationId: response.headers.get("x-correlation-id"),
    };
  }

  procedures(orgId: string, filter: { lifecycleState?: string; search?: string; limit?: number; offset?: number } = {}) {
    return this.request<{ procedures: SopProcedure[] }>("GET", orgId, `procedures${query(filter)}`);
  }
  procedure(orgId: string, procedureId: string) {
    return this.request<{ procedure: SopProcedure }>("GET", orgId, `procedures/${procedureId}`);
  }
  createProcedure(
    orgId: string,
    input: {
      title: string;
      purpose: string;
      category: string;
      criticality: SopCriticality;
      ownerUserId: string;
      dataClassification: SopDataClassification;
    },
  ) {
    return this.request<{ procedure: SopProcedure }>("POST", orgId, "procedures", input);
  }
  updateProcedure(
    orgId: string,
    procedureId: string,
    input: { version: number } & Partial<{
      title: string;
      purpose: string;
      category: string;
      criticality: SopCriticality;
      ownerUserId: string;
      dataClassification: SopDataClassification;
    }>,
  ) {
    return this.request<{ procedure: SopProcedure }>("PATCH", orgId, `procedures/${procedureId}`, input);
  }
  retireProcedure(orgId: string, procedureId: string, reason: string, version: number) {
    return this.request<{ procedure: SopProcedure }>("POST", orgId, `procedures/${procedureId}/retire`, { reason, version });
  }
  versions(orgId: string, procedureId: string) {
    return this.request<{ versions: SopProcedureVersion[] }>("GET", orgId, `procedures/${procedureId}/versions`);
  }
  version(orgId: string, procedureId: string, versionId: string) {
    return this.request<{ version: SopProcedureVersion }>("GET", orgId, `procedures/${procedureId}/versions/${versionId}`);
  }
  createVersion(
    orgId: string,
    procedureId: string,
    input: { expectedProcedureVersion: number; changeReason: string },
  ) {
    return this.request<{ version: SopProcedureVersion }>("POST", orgId, `procedures/${procedureId}/versions`, input);
  }
  updateVersion(
    orgId: string,
    procedureId: string,
    versionId: string,
    input: { version: number; changeReason?: string },
  ) {
    return this.request<{ version: SopProcedureVersion }>("PATCH", orgId, `procedures/${procedureId}/versions/${versionId}`, input);
  }
  replaceSteps(orgId: string, procedureId: string, versionId: string, steps: SopStepInput[], version: number) {
    return this.request<{ version: SopProcedureVersion }>(
      "PUT",
      orgId,
      `procedures/${procedureId}/versions/${versionId}/steps`,
      {
        version,
        steps: steps.map((step) => ({
          stepOrder: step.stepOrder,
          instruction: step.instruction,
          expectedInputs: step.expectedInputs ?? "",
          isBlocking: step.isBlocking ?? true,
          requiresEvidence: step.requiresEvidence ?? false,
          requiresVerification: step.requiresVerification ?? false,
        })),
      },
    );
  }
  private transitionVersion(orgId: string, procedureId: string, versionId: string, suffix: string, reason: string, version: number) {
    return this.request<{ version: SopProcedureVersion }>(
      "POST",
      orgId,
      `procedures/${procedureId}/versions/${versionId}/${suffix}`,
      { version, reason },
    );
  }
  submitVersion(orgId: string, procedureId: string, versionId: string, reason: string, version: number) {
    return this.transitionVersion(orgId, procedureId, versionId, "submit", reason, version);
  }
  approveVersion(orgId: string, procedureId: string, versionId: string, reason: string, version: number) {
    return this.transitionVersion(orgId, procedureId, versionId, "approve", reason, version);
  }
  publishVersion(orgId: string, procedureId: string, versionId: string, reason: string, version: number) {
    return this.transitionVersion(orgId, procedureId, versionId, "publish", reason, version);
  }
  returnVersionForChanges(orgId: string, procedureId: string, versionId: string, reason: string, version: number) {
    return this.transitionVersion(orgId, procedureId, versionId, "return-for-changes", reason, version);
  }
  runs(orgId: string, filter: { procedure?: string; state?: string; limit?: number; offset?: number } = {}) {
    return this.request<{ runs: SopRun[] }>("GET", orgId, `runs${query(filter)}`);
  }
  run(orgId: string, runId: string) {
    return this.request<{ run: SopRun }>("GET", orgId, `runs/${runId}`);
  }
  startRun(orgId: string, input: { versionId: string; subjectType?: string; subjectReference?: string }) {
    return this.request<{ run: SopRun }>("POST", orgId, "runs", input);
  }
  recordStep(
    orgId: string,
    runId: string,
    input: { stepId: string; version: number; result: SopStepResult; notes?: string },
  ) {
    return this.request<{ step: SopStepRecord }>("POST", orgId, `runs/${runId}/steps`, {
      stepId: input.stepId,
      version: input.version,
      result: input.result,
      notes: input.notes ?? "",
    });
  }
  runSteps(orgId: string, runId: string) {
    return this.request<{ steps: SopStepRecord[] }>("GET", orgId, `runs/${runId}/steps`);
  }
  verifyStep(orgId: string, runId: string, stepId: string, version: number) {
    return this.request<{ step: SopStepRecord }>("POST", orgId, `runs/${runId}/steps/${stepId}/verify`, { version });
  }
  attachEvidence(
    orgId: string,
    runId: string,
    input: { documentId: string; version: number; stepId?: string; note?: string },
  ) {
    return this.request<{ evidence: SopRunEvidence }>("POST", orgId, `runs/${runId}/evidence`, {
      documentId: input.documentId,
      version: input.version,
      note: input.note ?? "",
      ...(input.stepId === undefined ? {} : { stepId: input.stepId }),
    });
  }
  evidence(orgId: string, runId: string) {
    return this.request<{ evidence: SopRunEvidence[] }>("GET", orgId, `runs/${runId}/evidence`);
  }
  deviations(orgId: string, runId: string) {
    return this.request<{ deviations: SopDeviation[] }>("GET", orgId, `runs/${runId}/deviations`);
  }
  recordDeviation(
    orgId: string,
    runId: string,
    input: { version: number; whatWasDone: string; why: string; stepId?: string },
  ) {
    return this.request<{ deviation: SopDeviation }>("POST", orgId, `runs/${runId}/deviations`, {
      version: input.version,
      whatWasDone: input.whatWasDone,
      why: input.why,
      ...(input.stepId === undefined ? {} : { stepId: input.stepId }),
    });
  }
  disposeDeviation(
    orgId: string,
    runId: string,
    deviationId: string,
    decision: SopDeviationDecision,
    rationale: string,
  ) {
    if (!rationale.trim()) {
      return Promise.reject(new ApiError(400, "validation_error", "A rationale is required."));
    }
    return this.request<{ deviation: SopDeviation }>(
      "POST",
      orgId,
      `runs/${runId}/deviations/${deviationId}/dispose`,
      { decision, rationale },
    );
  }
  closeRun(orgId: string, runId: string, outcome: SopRunOutcome, version: number) {
    return this.request<{ run: SopRun }>("POST", orgId, `runs/${runId}/close`, { outcome, version });
  }
  controlLinks(orgId: string, procedureId: string) {
    return this.request<{ links: SopControlLink[] }>("GET", orgId, `procedures/${procedureId}/control-links`);
  }
  proposeControlLink(orgId: string, procedureId: string, controlId: string, note = "") {
    return this.request<{ link: SopControlLink }>("POST", orgId, `procedures/${procedureId}/control-links`, { controlId, note });
  }
  verifyControlLink(orgId: string, procedureId: string, linkId: string) {
    return this.request<{ link: SopControlLink }>("POST", orgId, `procedures/${procedureId}/control-links/${linkId}/verify`, {});
  }
  revokeControlLink(orgId: string, procedureId: string, linkId: string, reason: string) {
    return this.request<{ link: SopControlLink }>("POST", orgId, `procedures/${procedureId}/control-links/${linkId}/revoke`, { reason });
  }
  intelligencePublications(orgId: string, refusedOnly = false) {
    return this.request<{ publications: SopPublication[] }>("GET", orgId, `intelligence/publications${query({ refusedOnly: refusedOnly ? "true" : undefined })}`);
  }
  intelligenceAuditChain(orgId: string) {
    return this.request<Record<string, unknown>>("GET", orgId, "intelligence/audit-chain");
  }
  publishContracts(orgId: string) {
    return this.request<Record<string, unknown>>("POST", orgId, "intelligence/contracts", {});
  }
  claimConformance(orgId: string) {
    return this.request<Record<string, unknown>>("POST", orgId, "intelligence/conformance", {});
  }
  replayIntelligence(orgId: string) {
    return this.request<Record<string, unknown>>("POST", orgId, "intelligence/replay", {});
  }
}
